import { isNewNativeAppShell, parseAppShell } from "constants/appShell";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";

/**
 * Publishes web-owned state to the native shell.
 *
 * Three things the native UI needs live in this site's `localStorage`: the places you searched,
 * the properties you opened, and the uuid of your most recent lead. The shell used to *pull*
 * them — running `evaluateJavaScript` against the WebView and parsing the raw keys back out.
 * That coupled the app to this repo's key names and JSON shapes with no contract between them,
 * and it always raced the page, because React writes these keys in effects that run after the
 * navigation has already finished.
 *
 * So we push instead. The site owns the state, the site announces it.
 *
 * Always a **full snapshot**, never a delta: duplicate or out-of-order delivery is then harmless,
 * and a snapshot on page load doubles as the migration path for users whose history exists only
 * in `localStorage` — no one-time flag needed.
 *
 * Keys are still written exactly as before; this is purely additive. The site's own features
 * continue to read them.
 */

const RECENT_SEARCHES_KEY = "recentlyVisited";
const RECENTLY_VIEWED_KEY = "INVENTORIES_LAST_VIEWED";
const LEAD_SUCCESS_KEY = "lead-form-one-success-data";

/** Website caps: 8 searches (`addSearchedLocationToLocalStorage`), 10 viewed. */
const MAX_RECENT_SEARCHES = 8;
const MAX_RECENTLY_VIEWED = 10;

interface RecentSearchPayload {
  canonicalName: string;
  name: string;
  regionType?: string;
  country?: string;
}

export interface WebStateSnapshot {
  version: 1;
  recentSearches: RecentSearchPayload[];
  recentlyViewed: string[];
  leadUuid?: string;
}

const readJSON = (key: string): unknown => {
  try {
    const raw = window.localStorage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Corrupt or unreadable — treat as absent. The shell leaves the corresponding store
    // untouched rather than clearing it.
    return null;
  }
};

const readRecentSearches = (): RecentSearchPayload[] => {
  const raw = readJSON(RECENT_SEARCHES_KEY);
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item): item is Record<string, any> => !!item && typeof item === "object")
    .map((item) => ({
      canonicalName: String(item.canonical_name ?? ""),
      name: String(item.name ?? ""),
      regionType: item.region_type ? String(item.region_type) : undefined,
      country: item.country ? String(item.country) : undefined,
    }))
    .filter((item) => item.canonicalName && item.name)
    .slice(0, MAX_RECENT_SEARCHES);
};

const readRecentlyViewed = (): string[] => {
  const raw = readJSON(RECENTLY_VIEWED_KEY);
  if (!Array.isArray(raw)) return [];

  // `saveLastViewedListings` normalises to strings, but entries written by older builds can
  // still be objects carrying `canonical_name`.
  return raw
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") return String((item as any).canonical_name ?? "");
      return "";
    })
    .filter(Boolean)
    .slice(0, MAX_RECENTLY_VIEWED);
};

const readLeadUuid = (): string | undefined => {
  const raw = readJSON(LEAD_SUCCESS_KEY);
  if (!raw || typeof raw !== "object") return undefined;
  const { uuid } = raw as any;
  return typeof uuid === "string" && uuid ? uuid : undefined;
};

const buildSnapshot = (): WebStateSnapshot => {
  const snapshot: WebStateSnapshot = {
    version: 1,
    recentSearches: readRecentSearches(),
    recentlyViewed: readRecentlyViewed(),
  };
  const leadUuid = readLeadUuid();
  if (leadUuid) snapshot.leadUuid = leadUuid;
  return snapshot;
};

/**
 * True inside either new native shell (`ios_native`, `android_native`).
 *
 * Android does not consume `WEB_STATE` yet, but it discards unrecognised event types with a
 * debug log and returns — `AmberJsInterface.postMessage` checks `WebToNativeEvent.fromType`
 * and bails on null, the same shape as the iOS bridge. So publishing to it now is inert, and
 * the website needs no further change when Android adds its handler.
 */
const shouldPublish = (): boolean => {
  if (typeof window === "undefined") return false;
  return isNewNativeAppShell(parseAppShell(window.appShell));
};

let publishScheduled = false;

/**
 * Emits one `WEB_STATE` snapshot. Safe to call from anywhere that mutates the three keys, and
 * on page load.
 *
 * **The app-shell check lives here, not at the call sites.** There are eight of them across the
 * codebase and they all call this unconditionally, so no new one can forget the guard — the
 * failure mode of a missed check is a message posted into a web page that has no bridge. Outside
 * the native shells this returns on the first line and does nothing.
 *
 * Calls within the same tick coalesce into a single event, so a page that records a search and
 * a property view together sends one message rather than two.
 */
export const publishWebState = (): void => {
  if (!shouldPublish() || publishScheduled) return;

  publishScheduled = true;
  Promise.resolve().then(() => {
    publishScheduled = false;
    try {
      sendWebViewEvent(WEB_VIEW_APP_EVENTS.WEB_STATE, buildSnapshot());
    } catch {
      // A malformed storage value must never break the page.
    }
  });
};

export default publishWebState;
